'use client'

// React Imports
import { useState, useEffect, useMemo } from 'react'

// MUI Imports
import Checkbox from '@mui/material/Checkbox'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import LaunchIcon from '@mui/icons-material/Launch'
import Box from '@mui/material/Box'
import { MassOperationPanel } from '../../../../../components/common/MassOperation/MassOperation'

// Third-party Imports
import { Icon } from '@iconify/react'
import { createColumnHelper } from '@tanstack/react-table'
import { AnimatePresence } from 'framer-motion'

// Component Imports
import PremiumModuleWrapper from '../../common/PremiumModuleWrapper'
import OptionMenu from '../../../../../components/common/Template_Layouts/@core/components/option-menu'
import EntityEdit from '../../../../../components/common/EntityEdit/EntityEdit'
import ScoreCard from '../../../../../components/common/ScoreCard/ScoreCard'
import Loader from '../../../../../components/Loader/Loader'
import { IconList } from '../../../../Master_Settings/IconList'

// Util Imports
import { getInitials } from '../../../../../utils/helpers'
import { getStatusDescriptor, formatDate, TASK_ACTIONS } from '../../common/premiumUtils'
import API_ENDPOINTS from '../../../../../config/apiEndpoints'

const API_BASE_SERVICES = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || '').replace(/\/$/, '');
const ASSETS_BASE_URL = (process.env.REACT_APP_ASSETS_BASE_URL || '').replace(/\/$/, '');
const columnHelper = createColumnHelper()

const renderDynamicIcon = (iconPath, defaultIcon) => {
    if (iconPath && IconList && IconList[iconPath]) {
      return (
        <div 
          className="svg-icon-container" 
          dangerouslySetInnerHTML={{ __html: IconList[iconPath] }} 
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '26px', height: '26px' }}
        />
      );
    }
    if (iconPath && (iconPath.includes('/') || iconPath.includes('.'))) {
      let imgSrc = iconPath;
      if (!imgSrc.startsWith('http')) {
        imgSrc = `${ASSETS_BASE_URL}/${iconPath.replace(/^\//, '')}`;
      }
      return <img src={imgSrc} alt="icon" style={{ width: '26px', height: '26px', objectFit: 'contain' }} />;
    }
    if (iconPath) {
        return <i className={`fa ${iconPath}`} style={{ fontSize: '20px', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '26px', height: '26px' }}></i>;
    }
    return defaultIcon;
};

const TaskListWrapper = ({
    data: injectedData,
    stats: injectedStats,
    loading: injectedLoading,
    onAddTask,
    onEditTask,
    onDeleteTask,
    onMassDelete,
    onMassUpdate,
    onViewTask,
    totalRecords,
    currentPage,
    itemsPerPage,
    onPageChange,
    onStatusChange,
    onViewLead,
    onClearSelection,
    rowSelection = {},
    onManualRowSelectionChange,
    onSelectAll,
    onMarkAsCompleted,
    onRefresh,
    isInitialLoad,
    activeFilters = ['All'],
    searchTerm,
    crmOperationPerms,
    hideSelection = false,
    isHiddenField = () => false
}) => {
    // States
    const [showEntityEdit, setShowEntityEdit] = useState(false)
    const [selectedEntity, setSelectedEntity] = useState(null)

    const getAvatar = params => {
        const { avatar, name, threshold } = params
        const colorSchemes = [
            { bg: '#fdf2f8', text: '#db2777' }, // Pink
            { bg: '#f0fdf4', text: '#16a34a' }, // Green
            { bg: '#eff6ff', text: '#2563eb' }, // Blue
            { bg: '#f5f3ff', text: '#7c3aed' }, // Purple
            { bg: '#fff7ed', text: '#ea580c' }, // Orange
            { bg: '#fffbeb', text: '#d97706' }, // Amber
        ]
        const scheme = colorSchemes[(name || '').length % colorSchemes.length] || colorSchemes[2]

        return (
            <div className="lead-avatar-big">
                <div className="avatar-circle" style={{ backgroundColor: scheme.bg, color: scheme.text }}>
                    {avatar ? (
                        <img src={avatar} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                        getInitials(name || 'Unknown')
                    )}
                </div>
                <div className="score-badge-wrapper">
                    <span className="score-badge">{threshold ?? 0}</span>
                </div>
            </div>
        )
    }

    const columns = useMemo(() => [
        ...(hideSelection ? [] : [{
            id: 'select',
            header: ({ table }) => {
                const isAllPageSelected = table.getIsAllRowsSelected();
                return (
                    <Checkbox
                        checked={isAllPageSelected}
                        indeterminate={table.getIsSomeRowsSelected() && !isAllPageSelected}
                        onChange={(e) => {
                            if (isAllPageSelected) {
                                onManualRowSelectionChange({});
                            } else {
                                if (onSelectAll) onSelectAll();
                                else table.toggleAllRowsSelected(true);
                            }
                        }}
                        sx={{
                            color: '#d1d5db',
                            '&.Mui-checked': { color: '#0eb18f' },
                            '&.MuiCheckbox-indeterminate': { color: '#0eb18f' }
                        }}
                    />
                );
            },
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    disabled={!row.getCanSelect()}
                    indeterminate={row.getIsSomeSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    sx={{
                        color: '#d1d5db',
                        '&.Mui-checked': { color: '#0eb18f' }
                    }}
                />
            )
        }]),
        columnHelper.accessor('title', {
            header: 'TASK',
            cell: ({ row }) => {
                const statusLabel = getStatusDescriptor(
                    row.original.status,
                    row.original.dueDate,
                    row.original.completedDate,
                    row.original.taskTitle || row.original.title,
                    row.original.startDate || row.original.issuedDate,
                    row.original.statusLabel,
                    row.original.outcomeName,
                    row.original.createdDate
                )

                let chipColor = 'primary'
                if (statusLabel.includes('overdue')) chipColor = 'error'
                else if (statusLabel.includes('due today')) chipColor = 'warning'
                else if (row.original.status === 'Open') chipColor = 'info'
                else if (row.original.status === 'Completed') chipColor = 'success'

                return (
                    <div className='flex items-center gap-3'>
                        {renderDynamicIcon(row.original.iconClass || row.original.IconClass, (
                            <svg xmlns="http://www.w3.org/2000/svg" id="Capa_1" enableBackground="new 0 0 512 512" height="26px" viewBox="0 0 512 512" width="26px">
                                <g>
                                    <g>
                                        <path d="m338.554 452h-164.999c-12.407 0-22.5 10.093-22.5 22.5v37.5h142.5v-15h-127.5v-22.5c0-4.136 3.365-7.5 7.5-7.5h164.999c4.136 0 7.501 3.364 7.501 7.5v22.5h-37.499v15h52.499v-37.5c0-12.407-10.093-22.5-22.501-22.5z" fill="#3b82f6"></path>
                                        <path d="m256.055 353.932c45.491 0 82.5-37.009 82.5-82.5v-188.932c0-18.929-6.641-37.454-18.699-52.164l-11.6 9.51c9.865 12.035 15.299 27.183 15.299 42.653h-15v15h15v15h-15v15h15v15h-15v15h15v15h-15v15h15v15h-60.055v15h60.055v53.933c0 37.22-30.28 67.5-67.5 67.5s-67.5-30.28-67.5-67.5v-53.933h59.945v-15h-59.945v-15h44.998v-15h-44.998v-15h44.998v-15h-44.998v-15h44.998v-15h-44.998v-15h44.998v-15h-44.998c0-37.219 30.28-67.5 67.5-67.5 15.471 0 30.62 5.433 42.654 15.299l9.51-11.601c-14.709-12.056-33.235-18.697-52.164-18.697-45.491 0-82.5 37.01-82.5 82.5v188.931c0 45.491 37.009 82.501 82.5 82.501z" fill="#7dd7ff"></path>
                                        <path d="m398.555 202.499h-45v15h30v15h-30v15h15v23.934c0 62.033-50.468 112.5-112.5 112.5s-112.5-50.467-112.5-112.5v-23.934h14.89v-15h-30v-15h30v-15h-45v45h15.11v23.934c0 59.973 41.625 110.399 97.5 123.926v41.641h15v-38.955c4.922.579 9.925.887 15 .887s10.079-.307 15-.887v38.955h15v-41.643c55.875-13.527 97.5-63.953 97.5-123.926v-23.934h15z" fill="#7dd7ff"></path>
                                        <path d="m226.055 278.933c0 16.542 13.458 30 30 30s30-13.458 30-30-13.458-30-30-30-30 13.458-30 30zm30-15c8.271 0 15 6.729 15 15s-6.729 15-15 15-15-6.729-15-15 6.729-15 15-15z" fill="#7dd7ff"></path>
                                        <path d="m248.552 37.501h15v15h-15z" fill="#7dd7ff"></path>
                                        <path d="m293.555 232.498h15v15h-15z" fill="#7dd7ff"></path>
                                        <path d="m278.555 142.5h15v15h-15z" fill="#7dd7ff"></path>
                                        <path d="m278.555 172.499h15v15h-15z" fill="#7dd7ff"></path>
                                        <path d="m278.555 112.5h15v15h-15z" fill="#7dd7ff"></path>
                                        <path d="m278.555 82.5h15v15h-15z" fill="#7dd7ff"></path>
                                    </g>
                                </g>
                            </svg>
                        ))}
                        <div className='flex flex-col gap-1'>
                            <div className='flex items-center gap-2 flex-wrap'>
                                <Typography
                                    sx={{ color: '#666cff', fontSize: '16px', fontWeight: 600, textDecoration: 'underline' }}
                                    className='line-clamp-1 cursor-pointer'
                                    onClick={() => onViewTask && onViewTask(row.original)}
                                >
                                    {row.original.taskTitle || row.original.title || 'No Title'}
                                </Typography>
                                <Chip
                                    label={statusLabel}
                                    color={chipColor}
                                    variant='filled'
                                    size='small'
                                    className='font-medium text-[10px] h-[20px] px-2'
                                    sx={{
                                        color: '#fff !important',
                                        backgroundColor: (theme) => {
                                            const lowerLabel = statusLabel.toLowerCase();
                                            if (chipColor === 'error' || lowerLabel.includes('overdue')) return '#ea5455';
                                            if (chipColor === 'success' || lowerLabel.includes('completed')) return '#28c76f';
                                            if (chipColor === 'warning' || lowerLabel.includes('due today')) return '#ff9f43';
                                            if (chipColor === 'info' || row.original.status === 'Open') return '#3b82f6';
                                            return undefined;
                                        }
                                    }}
                                />
                            </div>

                            {row.original.outcomeName && (
                                <Typography sx={{ fontSize: '12.5px', color: '#28a745', fontWeight: 600 }}>
                                    Outcome: {row.original.outcomeName}
                                </Typography>
                            )}
                            {row.original.completedDate && (row.original.status === 'Completed' || row.original.status === 'Closed') && (
                                <Typography sx={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
                                    Completed: {formatDate(row.original.completedDate, false)}
                                </Typography>
                            )}
                        </div>
                    </div>
                )
            }
        }),
        columnHelper.accessor('dueDate', {
            header: 'DUE DATE',
            cell: ({ row }) => (
                <Typography
                    sx={{ fontSize: '14px', fontWeight: 600, color: '#334155 !important', opacity: 1 }}
                >
                    {formatDate(row.original.dueDate, false)}
                </Typography>
            )
        }),
        columnHelper.accessor('relatedTo', {
            header: 'RELATED TO',
            cell: ({ row }) => (
                <div className='flex items-center gap-3'>
                    <ScoreCard
                        leadId={row.original.enquiryId}
                        mobileNo={row.original.phone}
                        personName={row.original.relatedTo}
                    >
                        <div className='flex-shrink-0'>
                            {getAvatar({ avatar: row.original.avatar, name: row.original.relatedTo, threshold: row.original.threshold })}
                        </div>
                    </ScoreCard>
                    <div className='flex flex-col gap-0.5'>
                        <div className='flex items-center gap-2'>
                            <Typography
                                className='cursor-pointer'
                                sx={{ color: '#666cff !important', fontSize: '15px', fontWeight: 600, margin: 0, textDecoration: 'underline' }}
                                onClick={() => onViewLead && onViewLead(row.original.enquiryId)}
                            >
                                {row.original.relatedTo}
                            </Typography>
                            <LaunchIcon
                                sx={{
                                    fontSize: '1rem',
                                    cursor: 'pointer',
                                    color: '#94a3b8',
                                    '&:hover': { color: '#666cff' }
                                }}
                                onClick={(e) => { e.stopPropagation(); setSelectedEntity(row.original); setShowEntityEdit(true); }}
                                titleAccess="Edit Entity"
                            />
                        </div>
                        <Typography sx={{ color: '#1e293b', fontSize: '13px', fontWeight: 500, opacity: 0.8 }}>
                            {row.original.phone}
                        </Typography>
                    </div>
                </div>
            )
        }),
        columnHelper.accessor('owner', {
            header: 'OWNER',
            cell: ({ row }) => (
                <div className='flex items-center gap-3'>
                    <div className='flex-shrink-0 w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center bg-white shadow-sm ring-2 ring-slate-50'>
                        <Icon icon='ri:user-3-line' fontSize={18} className='text-slate-500' />
                    </div>
                    <Typography sx={{ fontSize: '14.5px', fontWeight: 600, color: '#333155 !important', opacity: 1 }}>
                        {row.original.owner}
                    </Typography>
                </div>
            )
        }),
        columnHelper.accessor('action', {
            header: 'ACTION',
            cell: ({ row }) => {
                const isCompleted = row.original.status?.toLowerCase() === 'completed';

                let settingActions = [];
                
                // Helper to check row-level API permissions safely
                const checkRowPerm = (permFlag) => {
                    const val = row.original?.[permFlag];
                    if (val === undefined || val === null) return true; // default to true if API doesn't send it
                    return val !== false && String(val).toLowerCase() !== 'false';
                };

                if (onViewTask && checkRowPerm('hasView')) {
                    settingActions.push({
                        text: 'View',
                        icon: <Icon icon='tabler:eye' fontSize={20} style={{ color: '#3b82f6' }} />,
                        menuItemProps: { className: 'flex items-center gap-2', onClick: () => onViewTask(row.original) }
                    });
                }

                if (onDeleteTask && checkRowPerm('hasRemove')) {
                    settingActions.push({
                        text: 'Remove',
                        icon: <Icon icon='tabler:trash' fontSize={20} style={{ color: '#ef4444' }} />,
                        menuItemProps: { className: 'flex items-center gap-2', onClick: () => onDeleteTask(row.original) }
                    });
                }

                if (!isCompleted) {
                    if (onEditTask) {
                        settingActions.push({
                            text: 'Edit',
                            icon: <Icon icon='tabler:edit' fontSize={20} style={{ color: '#6366f1' }} />,
                            menuItemProps: { className: 'flex items-center gap-2', onClick: () => onEditTask(row.original) }
                        });
                    }
                    if (onMarkAsCompleted && checkRowPerm('hasCompletion')) {
                        settingActions.push({
                            text: 'Mark As Complete',
                            icon: <Icon icon='tabler:book' fontSize={20} style={{ color: '#10b981' }} />,
                            menuItemProps: { className: 'flex items-center gap-2', onClick: () => onMarkAsCompleted(row.original) }
                        });
                    }
                    if (onViewTask && checkRowPerm('hasComment')) {
                        settingActions.push({
                            text: 'Comment',
                            icon: <Icon icon='tabler:message-2' fontSize={20} style={{ color: '#f59e0b' }} />,
                            menuItemProps: { className: 'flex items-center gap-2', onClick: () => onViewTask(row.original, 'comment') }
                        });
                    }
                }

                const crmActions = TASK_ACTIONS.filter(action => {
                    if (!crmOperationPerms) return true;
                    switch(action.id) {
                        case 'followup': return crmOperationPerms.AddFollowup !== false;
                        case 'send-mail': return crmOperationPerms.SendMail !== false;
                        case 'send-sms': return crmOperationPerms.SendSms !== false;
                        case 'send-voice': return crmOperationPerms.SendVoice !== false;
                        case 'add-notes': return crmOperationPerms.AddNote !== false;
                        case 'add-task': return crmOperationPerms.AddTask !== false;
                        case 'add-appointment': return crmOperationPerms.AddAppointment !== false;
                        case 'add-deal': return crmOperationPerms.AddDeal !== false;
                        case 'upload-document': return crmOperationPerms.UploadDocument !== false;
                        case 'fill-webform': return crmOperationPerms.AddWebform !== false;
                        default: return true;
                    }
                }).map(action => ({
                    text: action.label,
                    icon: <Icon icon={action.icon} fontSize={20} style={{ color: action.color }} />,
                    menuItemProps: {
                        className: 'flex items-center gap-2',
                        onClick: () => onViewTask && onViewTask(row.original, action.id)
                    }
                }));

                return (
                    <div className='flex items-center justify-center gap-2 w-full min-w-[100px]'>
                        {settingActions.length > 0 && (
                            <OptionMenu
                                iconClassName='text-textSecondary'
                                icon={<Icon icon='ri:more-2-fill' fontSize={22} style={{ color: '#64748b' }} />}
                                iconButtonProps={{
                                    sx: {
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '8px',
                                        padding: '6px',
                                        '&:hover': { backgroundColor: '#f1f5f9', borderColor: '#cbd5e1' }
                                    }
                                }}
                                options={settingActions}
                            />
                        )}
                        {crmActions.length > 0 && (
                            <OptionMenu
                                iconClassName='text-textSecondary'
                                icon={<Icon icon='tabler:settings' fontSize={20} style={{ color: '#64748b' }} />}
                                iconButtonProps={{
                                    sx: {
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '8px',
                                        padding: '7px',
                                        '&:hover': { backgroundColor: '#f1f5f9', borderColor: '#cbd5e1' }
                                    }
                                }}
                                options={crmActions}
                            />
                        )}
                    </div>
                );
            },
            enableSorting: false
        })
    ], [onViewTask, onEditTask, onDeleteTask, onViewLead, hideSelection, isHiddenField]);

    // Filter out hidden columns based on API mappings
    const finalColumns = columns.filter(col => {
        if (col.id === 'title') return !isHiddenField('Subject');
        if (col.id === 'dueDate') return !isHiddenField('DueDate');
        if (col.id === 'relatedTo') return !isHiddenField('PersonName');
        if (col.id === 'owner') return !isHiddenField('Owner');
        return true;
    });

    // Rely on injectedData exclusively
    const finalData = injectedData || [];
    const finalStats = useMemo(() => {
        // We strictly use the global stats provided by the parent (TaskModule).
        // If the stats aren't loaded yet, we show 0 rather than falling back to filtered counts.
        const baseStats = injectedStats || { total: 0, completed: 0, overdue: 0, open: 0 };
        
        return {
            total: baseStats.total || 0,
            completed: baseStats.completed || 0,
            overdue: baseStats.overdue || 0,
            open: baseStats.open || 0
        };
    }, [injectedStats]);

    // Non-blocking loading logic: We pass the loading state to PremiumModuleWrapper
    // which handles the overlay internally, or we render an overlay here.
    return (
        <Box sx={{ position: 'relative', width: '100%', minHeight: '400px', flex: 1, display: 'flex', flexDirection: 'column' }}>

            <PremiumModuleWrapper
                moduleName="Task"
                stats={finalStats}
                onStatusClick={onStatusChange}
                data={finalData}
                columns={finalColumns}
                totalRecords={totalRecords}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onPageChange={onPageChange}
                onManualRowSelectionChange={onManualRowSelectionChange}
                rowSelection={rowSelection}
                activeFilters={activeFilters}
                loading={injectedLoading}
                showCards={false}
            />
            {showEntityEdit && (
                <EntityEdit
                    entityData={selectedEntity}
                    onSave={() => {
                        if (onRefresh) onRefresh();
                        setShowEntityEdit(false);
                    }}
                    onCancel={() => setShowEntityEdit(false)}
                />
            )}
        </Box>
    )
}

export default TaskListWrapper
